/* eslint-disable max-lines-per-function */
import { FeedItem, FeedType } from 'types/feeds';
import { useTranslation } from 'react-i18next';
import { PageContext } from 'src/context/PageContext';
import { useContext, useState } from 'react';
import { ImageBoss } from 'components/Content/Atoms/ImageBoss';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { RenderMarkdownMarked } from 'components/Atoms/RenderMarkdownMarked/RenderMarkdownMarked';
import { ContentType } from 'types/publishedAssets';
import { useLocalizedDate } from 'utils/useLocalizedDate';
import { useIsRTL } from 'utils/useIsRTL';
import { HeroIllustration } from 'components/Content/ContentTypes/Hero/HeroIllustration';
import { isCollabMapEntity, resolvePreviewEntity, PreviewLinkTarget } from '@/constants/previewEntities';
import { buildPreviewAwarePath } from 'utils/previewLink';
import styles from './FeedItemCard.module.scss';
import { ActivityDateChip } from './ActivityDateChip';
import { ParticipationIcon } from './ParticipationIcon';
import { StatusBadge } from './StatusBadge';
import { FeedItemConditionalLink } from './FeedItemConditionalLink';
import { NotificationSignupDialog } from '../NotificationSignup/NotificationSignupDialog';

interface FeedItemCardProps {
  data: FeedItem;
  type: FeedType;
  variant?: 'imageTop' | 'imageLeft';
  isTwoColumns?: boolean;
  participationCount?: number;
  content: ContentType;
  /** Mixed-list tiles default update CTAs to "Read More" — "View Update" reads wrong beside projects and info tiles (ENG-4460). */
  isMixedList?: boolean;
}

export function FeedItemCard(props: FeedItemCardProps) {
  const { data, type, variant = 'imageTop', isTwoColumns, participationCount, content, isMixedList } = props;
  const { t } = useTranslation();
  const { urlParams, linkHostname, slug: pageSlug } = useContext(PageContext);
  const { isRTL } = useIsRTL();
  const isCollabMap = isCollabMapEntity(resolvePreviewEntity(pageSlug, urlParams));
  const isActivity = type === 'engagement_activity';
  const {
    id,
    slug,
    status,
    buttonLabel: mainButtonLabel,
    image: mainImage,
    imageAltText: mainImageAltText,
    title: mainTitle,
    description: mainDescription,
    projectTitle: mainProjectTitle,
    startTime,
    endTime,
    sourceTable,
    projectSlug,
    type: activityType,
    reportUrl,
    fallbackTitle,
    fallbackDescription,
    fallbackButtonLabel,
    fallbackImage,
    fallbackImageAltText,
    fallbackProjectTitle,
    releasedAt,
    hideDynamicContent,
  } = data;
  const image = mainImage || fallbackImage;
  const imageAltText = mainImageAltText || fallbackImageAltText;
  const title = mainTitle || fallbackTitle;
  const buttonLabel = mainButtonLabel || fallbackButtonLabel;
  const description = mainDescription || fallbackDescription;
  const projectTitle = mainProjectTitle || fallbackProjectTitle;
  // special case to hide images for updates that are in column two
  const isColumnTwo = content.entity_order?.toString()?.split('.')?.[1] === '0001';
  const shouldHideImage = isColumnTwo && type === 'update' && isTwoColumns;
  const [showNotificationSignup, setShowNotificationSignup] = useState(false);
  const showNotificationSignupCondition = status === 'upcoming' && type === 'engagement_activity';
  /**
   * Whether *this tile* is a collaborative map, which is not what `isCollabMap` above answers —
   * that one describes the page the feed is rendered on.
   */
  const isCollabMapTile = isActivity && activityType === 'CollaborativeMap';
  /** A closed map with no report is the one case that offers "View Map" rather than a report. */
  const showsViewMap = isCollabMapTile && status === 'closed' && !reportUrl;
  const formattedReleaseDate = useLocalizedDate(releasedAt!, 'MMMM d, yyyy');
  const showDynamicContent = !hideDynamicContent;
  const showPostedRow = showDynamicContent && type === 'update';
  const showProjectRow = showDynamicContent && !!projectTitle;

  const getActivityButtonLabel = () => {
    switch (status) {
      case 'open':
        if (isCollabMap) {
          return t('feed.dropAPin');
        }
        return t('feed.participateNow');
      case 'upcoming':
        return t('feed.getNotified');
      case 'closed':
        // A closed collaborative map is still worth opening — the status badge already says it
        // has closed, so the call to action invites a resident to read the pins (ENG-4529).
        if (showsViewMap) return t('collaborativeMap.viewMap');
        return reportUrl ? t('feed.viewResults') : undefined;
      default:
        return undefined;
    }
  };
  const getButtonLabel = () => {
    if (buttonLabel) return buttonLabel;
    if (sourceTable === 'IdeaBoard') {
      return t('feed.viewBoard');
    }
    if (sourceTable === 'DiscussionTopic') {
      return t('feed.viewDiscussion');
    }
    switch (type) {
      case 'engagement_activity':
        return getActivityButtonLabel();
      case 'project':
      case 'hub':
        return t('feed.learnMore');
      case 'update':
        return isMixedList ? t('feed.readMore') : t('feed.viewUpdates');
      default:
        return undefined;
    }
  };
  const cardClassName = () => {
    if (isTwoColumns) return `${styles.feedItemCard} ${styles.imageTopVariant} ${styles.feedItemCardFullWidth}`;
    if (variant === 'imageLeft') return `${styles.feedItemCard} ${styles.imageLeftVariant}`;
    return `${styles.feedItemCard} ${styles.imageTopVariant}`;
  };
  const getActionUrl = () => {
    const { organization, locale } = urlParams || {};
    if (!organization || !locale) return '';
    const railsLanguage = locale?.startsWith('zh-') ? locale : locale?.split('-')[0];
    if (sourceTable === 'DiscussionTopic') {
      if (!projectSlug || !slug) return '';
      // special case for discussion topic for rails
      if (globalThis.process.env.NEXT_PUBLIC_APP_ENV === 'production') {
        return `https://${organization}.civilspace.io/${railsLanguage}/projects/${projectSlug}/discussion_topics/${slug}`;
      }
      return `https://${organization}.civilspace.dev/${railsLanguage}/projects/${projectSlug}/discussion_topics/${slug}`;
    }
    if (sourceTable === 'IdeaBoard') {
      if (!projectSlug || !slug) return '';
      // special case for idea board for rails
      if (globalThis.process.env.NEXT_PUBLIC_APP_ENV === 'production') {
        return `https://${organization}.civilspace.io/${railsLanguage}/projects/${projectSlug}/idea_boards/${slug}`;
      }
      return `https://${organization}.civilspace.dev/${railsLanguage}/projects/${projectSlug}/idea_boards/${slug}`;
    }
    const buildContentPath = (rest: string[], target: PreviewLinkTarget) =>
      buildPreviewAwarePath([organization, locale, ...rest], target, { urlParams, slug: pageSlug, linkHostname });

    if (status === 'closed') {
      if (reportUrl) return reportUrl;
      // Everything else closed has nowhere to go, but a collaborative map's pins are a public
      // record of the consultation and stay readable after it closes. Without this the tile
      // renders with no link at all and the map becomes unreachable (ENG-4529).
      return isCollabMapTile ? buildContentPath(['collabmap', slug || id], 'activity') : '';
    }
    if (activityType === 'LiveEngagement') return '';
    if (status === 'upcoming' && type === 'engagement_activity') {
      return '';
    }
    switch (type) {
      case 'engagement_activity':
        if (activityType === 'CollaborativeMap') {
          return buildContentPath(['collabmap', slug || id], 'activity');
        }
        // TODO: when engagements support slugs in future change url to use slug but for now only id is supported
        return buildContentPath(['engagements', id], 'activity');
      case 'project':
        return buildContentPath(['projects', slug || id], 'page');
      case 'update':
      case 'hub':
        return buildContentPath(['pages', slug || id], 'page');
      default:
        return buildContentPath([], 'page');
    }
  };
  // "View Map" sends a reader somewhere to look, like a board or a discussion, so it takes the
  // same filled treatment. The muted secondary style stays with "View Results", which leaves the
  // site for a report and reads the same on every activity type.
  const showParticipationStyleButton =
    (status === 'open' && type !== 'update') ||
    showsViewMap ||
    sourceTable === 'DiscussionTopic' ||
    sourceTable === 'IdeaBoard';
  return (
    <>
      <FeedItemConditionalLink
        href={getActionUrl()}
        openInNewTab={!!reportUrl && status === 'closed'}
        onClick={
          showNotificationSignupCondition
            ? () => {
                setShowNotificationSignup(!showNotificationSignup);
              }
            : undefined
        }
      >
        <div className={cardClassName()}>
          {!shouldHideImage && (
            <div className={styles.imageContainer}>
              {image && variant !== 'imageLeft' && (
                <ImageBoss url={image} alt={imageAltText || title!} className={styles.image} width={370} height={190} />
              )}
              {image && variant === 'imageLeft' && (
                <ImageBoss url={image} alt={imageAltText || title!} className={styles.image} width={400} height={250} />
              )}
              {!image && <HeroIllustration variant="dark" className={styles.imageIllustration} isRTL={isRTL} />}
            </div>
          )}

          <div className={styles.content}>
            <div className={styles.topSection}>
              {isActivity && (
                <div className={styles.statusContainer}>
                  <div className={styles.statusBadge}>
                    <StatusBadge status={status} />
                  </div>
                  {showDynamicContent && <ActivityDateChip status={status} startTime={startTime} endTime={endTime} />}
                </div>
              )}
              <h3 className={styles.title}>{title}</h3>
              {description && (
                <div className={styles.description}>
                  <RenderMarkdownMarked text={description} />
                </div>
              )}
            </div>

            <div className={styles.bottomSection}>
              <div className={styles.metadataContainer}>
                {showPostedRow && (
                  <div className={styles.metadataRow}>
                    <span className={styles.metadataLabel}>{t('feed.postedLabel')}</span>
                    <span>{formattedReleaseDate}</span>
                  </div>
                )}
                {showProjectRow && (
                  <span className={styles.metadataRow}>
                    <span className={styles.metadataLabel}>{t('feed.projectLabel')}</span>
                    <span>{projectTitle}</span>
                  </span>
                )}
              </div>
              <div className={styles.cardFooter}>
                {(getActionUrl() || showNotificationSignupCondition) && (
                  <ActionButton
                    className={styles.actionButton}
                    type={showParticipationStyleButton ? ButtonType.PARTICIPATION : ButtonType.ACTION_SECONDARY}
                  >
                    {getButtonLabel()}
                  </ActionButton>
                )}
                {showDynamicContent && isActivity && !!participationCount && activityType !== 'LiveEngagement' ? (
                  <div className={styles.participationCountContainer}>
                    <ParticipationIcon />
                    <span>{participationCount}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </FeedItemConditionalLink>
      {showNotificationSignup && (
        <NotificationSignupDialog
          popupOpen={showNotificationSignup}
          setPopupOpen={setShowNotificationSignup}
          residentCustomPublicationIds={content.metadata.resident_custom_publication_ids}
        />
      )}
    </>
  );
}
